import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ensureSessionHeaders } from '../../lib/serverSession';
import { resolveProofUrl } from '../../lib/proofUrlResolver';
import { ManualRecordEditor } from './ManualRecordEditor';
import { FullFinancialAuditReport } from './FullFinancialAuditReport';
import { AdminUserEditor } from './AdminUserEditor';
import { LockedWalletAdmin } from './LockedWalletAdmin';
import { ReversePaymentModal } from './ReversePaymentModal';
import { ManualRecordCreator } from './ManualRecordCreator';
import { CommissionRecordCreator } from './CommissionRecordCreator';
import { UserAuditPanel } from './UserAuditPanel';
import { PersonalActivityReport } from './PersonalActivityReport';
import { displayRowBonusShares } from '../../lib/shareRowDisplay';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

// Chart color palette
const CHART_COLORS = ['#F59E0B', '#10B981', '#3B82F6', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

// Date range presets
const DATE_PRESETS = [
  { label: 'Today', getValue: () => { const d = new Date(); return { start: d.toISOString().split('T')[0], end: d.toISOString().split('T')[0] }; } },
  { label: 'Last 7 Days', getValue: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 7); return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] }; } },
  { label: 'Last 30 Days', getValue: () => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - 30); return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] }; } },
  { label: 'This Month', getValue: () => { const d = new Date(); return { start: new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0], end: d.toISOString().split('T')[0] }; } },
  { label: 'Last Month', getValue: () => { const d = new Date(); const s = new Date(d.getFullYear(), d.getMonth() - 1, 1); const e = new Date(d.getFullYear(), d.getMonth(), 0); return { start: s.toISOString().split('T')[0], end: e.toISOString().split('T')[0] }; } },
  { label: 'This Quarter', getValue: () => { const d = new Date(); const q = Math.floor(d.getMonth() / 3); return { start: new Date(d.getFullYear(), q * 3, 1).toISOString().split('T')[0], end: d.toISOString().split('T')[0] }; } },
  { label: 'This Year', getValue: () => { const d = new Date(); return { start: new Date(d.getFullYear(), 0, 1).toISOString().split('T')[0], end: d.toISOString().split('T')[0] }; } },
  { label: 'All Time', getValue: () => ({ start: '', end: '' }) }
];

// Auto-refresh intervals
const REFRESH_INTERVALS = [
  { label: 'Off', value: 0 },
  { label: '30 seconds', value: 30000 },
  { label: '1 minute', value: 60000 },
  { label: '5 minutes', value: 300000 }
];

interface AuditSummary {
  totalUsers: number;
  totalUSDTInSystem: number;
  totalSharesIssued: number;
  totalCommissionsPaid: number;
  totalEscrowBalance: number;
  totalAvailableUSDT: number;
}

interface AuditRecord {
  user_id: number;
  user_email: string;
  user_username: string | null;
  user_phone: string | null;
  user_name: string;
  user_country: string | null;
  usd_purchase_crypto: number;
  usd_purchase_zar: number;
  total_purchased_usdt: number;
  shares_purchased_monetary: number;
  shares_from_commission: number;
  total_shares_decimal: number;
  commission_earned_usdt: number;
  commission_converted_to_shares: number;
  commission_withdrawals: number;
  available_balance_usdt: number;
  // Legacy fields (kept for backward compat)
  bonus_shares_15_percent: number;
  additional_bonuses: number;
  escrow_balance_usdt: number;
  total_shares_integer: number;
  // New reconciliation fields
  withdrawal_fees: number;
  admin_adjustments: number;
  payment_methods_summary: string;
  competition_prizes: number;
  manual_admin_additions: number;
  // Alias for backward compat
  commission_earned_usdt_15_percent?: number;
  // KYC fields
  kyc_status: string;
  kyc_country_code: string | null;
  kyc_country_name: string | null;
  kyc_full_name: string | null;
  kyc_id_type: string | null;
  kyc_email: string | null;
  kyc_phone: string | null;
  kyc_city: string | null;
  kyc_address: string | null;
  kyc_postal_code: string | null;
  kyc_created_at: string | null;
  kyc_updated_at: string | null;
  // Account state (owner-only delete eligibility)
  account_status?: string | null;
  is_active?: boolean | null;
}

interface SharePurchase {
  purchase_id: string;
  purchase_date: string;
  package_name: string;
  shares_purchased: number;
  total_amount: number;
  payment_method: string;
  transaction_reference: string | null;
  status: string;
  phase_id: number | null;
  price_per_share: number | null;
  bonus_shares: number;
  display_bonus_shares?: number;
  created_at: string;
  updated_at: string;
}

interface CommissionTransaction {
  transaction_id: string;
  referred_user_id: number;
  referred_user_email: string | null;
  referred_user_name: string | null;
  share_purchase_id: string;
  share_purchase_amount: number;
  commission_rate: number;
  usdt_commission: number;
  share_commission: number;
  status: string;
  payment_date: string;
  created_at: string;
  phase_id: number | null;
}

interface CommissionConversion {
  conversion_id: string;
  shares_requested: number;
  usdt_amount: number;
  share_price: number;
  phase_id: number;
  phase_number: number;
  status: string;
  approved_by_admin_id: number | null;
  approved_at: string | null;
  rejected_by_admin_id: number | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

interface ShareTransfer {
  transfer_id: string;
  transfer_type: string;
  other_user_id: number;
  other_user_email: string | null;
  other_user_name: string | null;
  shares_transferred: number;
  transfer_reason: string;
  status: string;
  created_at: string;
  completed_at: string | null;
}

interface TransactionProof {
  id: string;
  transaction_hash?: string;
  sender_wallet?: string;
  receiver_wallet?: string;
  network?: string;
  screenshot_url?: string;
  payment_method?: string;
  amount?: number;
  currency?: string;
  status?: string;
  created_at?: string;
}

interface UserDetails {
  purchases: SharePurchase[];
  commissions: CommissionTransaction[];
  conversions: CommissionConversion[];
  transfers: ShareTransfer[];
}

// Advanced Filter Interface
interface AdvancedFilters {
  email: string;
  userId: string;
  minAmount: string;
  maxAmount: string;
  transactionType: string;
  startDate: string;
  endDate: string;
  country: string;
  kycStatus: string;
  vulnerabilityFilter: string;
}

export function MasterAuditEnhanced({ currentUser: _currentUser }: { currentUser?: any }) {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [records, setRecords] = useState<AuditRecord[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<AuditRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'purchases' | 'commissions' | 'conversions' | 'transfers'>('purchases');
  const [proofModalOpen, setProofModalOpen] = useState(false);
  const [selectedProof, setSelectedProof] = useState<TransactionProof | null>(null);
  const [proofLoading, setProofLoading] = useState(false);
  const [editingRecord, setEditingRecord] = useState<any>(null);
  const [creatingRecord, setCreatingRecord] = useState(false);
  const [recordType, setRecordType] = useState<string>('');
  const [auditingUser, setAuditingUser] = useState<{ userId: number; username: string } | null>(null);
  const [johanFullAuditUser, setJohanFullAuditUser] = useState<{ userId: number; username: string } | null>(null);
  const [johanEditUser, setJohanEditUser] = useState<{ userId: number; username: string } | null>(null);
  const [johanWalletUser, setJohanWalletUser] = useState<{ userId: number; username: string } | null>(null);
  // Owner-only (Johan/JP/Ruvan) actions surfaced in the user-row dropdown.
  const [reverseUser, setReverseUser] = useState<{ userId: number; username: string } | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [currentUserId] = useState<number | null>(
    typeof _currentUser?.database_user?.id === 'number'
      ? _currentUser.database_user.id
      : typeof _currentUser?.user_metadata?.user_id === 'number'
        ? _currentUser.user_metadata.user_id
        : null
  );
  const currentAdminEmail = String(_currentUser?.email || _currentUser?.database_user?.email || '');
  const isJohan = currentAdminEmail.toLowerCase() === 'johan@beaversden.co.za';
  // Owner trio (mirrors scrub/reassign RPC gates): Johan, JP, Ruvan.
  const OWNER_EMAILS = ['johan@beaversden.co.za', 'jp.rademeyer84@gmail.com', 'krimmel1990@gmail.com'];
  const isOwner = OWNER_EMAILS.includes(currentAdminEmail.toLowerCase());
  // Delete eligibility in Master Audit: suspended OR quarantined accounts only.
  const isDeletable = (r: AuditRecord) => r.account_status === 'suspended' || r.account_status === 'quarantined';

  // Delete operation loading state
  const [deletingCommissionId, setDeletingCommissionId] = useState<string | null>(null);

  // Advanced Filters State
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filters, setFilters] = useState<AdvancedFilters>({
    email: '',
    userId: '',
    minAmount: '',
    maxAmount: '',
    transactionType: '',
    startDate: '',
    endDate: '',
    country: '',
    kycStatus: '',
    vulnerabilityFilter: ''
  });

  // Main view tab
  const [mainView, setMainView] = useState<'audit' | 'golddiggers'>('audit');
  const [goldDiggersData, setGoldDiggersData] = useState<any[]>([]);
  const [goldDiggersLoading, setGoldDiggersLoading] = useState(false);

  // ===== NEW UPGRADE FEATURES STATE =====
  // Charts
  const [showCharts, setShowCharts] = useState(false);
  const [chartType, setChartType] = useState<'pie' | 'bar'>('pie');

  // Export states
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  // Auto-refresh
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);
  const autoRefreshRef = useRef<NodeJS.Timeout | null>(null);

  // Sorting
  const [sortColumn, setSortColumn] = useState<keyof AuditRecord | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Search ref for keyboard shortcuts
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!currentUserId) {
      setError('You must be logged in as an admin to view the Master Audit.');
      return;
    }
    loadAuditData();
    // Load saved filters from localStorage with robust error handling
    try {
      const savedFilters = localStorage.getItem('masterAuditFilters');
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        // Validate the parsed object has expected structure
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setFilters({
            email: typeof parsed.email === 'string' ? parsed.email : '',
            userId: typeof parsed.userId === 'string' ? parsed.userId : '',
            minAmount: typeof parsed.minAmount === 'string' ? parsed.minAmount : '',
            maxAmount: typeof parsed.maxAmount === 'string' ? parsed.maxAmount : '',
            transactionType: typeof parsed.transactionType === 'string' ? parsed.transactionType : '',
            startDate: typeof parsed.startDate === 'string' ? parsed.startDate : '',
            endDate: typeof parsed.endDate === 'string' ? parsed.endDate : '',
            country: typeof parsed.country === 'string' ? parsed.country : '',
            kycStatus: typeof parsed.kycStatus === 'string' ? parsed.kycStatus : '',
            vulnerabilityFilter: typeof parsed.vulnerabilityFilter === 'string' ? parsed.vulnerabilityFilter : ''
          });
        }
      }
    } catch (e) {
      console.error('Failed to load saved filters, resetting to defaults:', e);
      // Clear corrupt data from localStorage
      try {
        localStorage.removeItem('masterAuditFilters');
      } catch (clearError) {
        console.error('Failed to clear corrupt localStorage:', clearError);
      }
    }
  }, [currentUserId]);

  useEffect(() => {
    filterRecords();
  }, [searchTerm, records]);

  // Save filters to localStorage whenever they change (with error handling)
  useEffect(() => {
    try {
      localStorage.setItem('masterAuditFilters', JSON.stringify(filters));
    } catch (e) {
      console.error('Failed to save filters to localStorage:', e);
    }
  }, [filters]);

  // Debounced filter application
  useEffect(() => {
    const timer = setTimeout(() => {
      applyAdvancedFilters();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  // ===== AUTO-REFRESH EFFECT =====
  useEffect(() => {
    if (autoRefreshRef.current) {
      clearInterval(autoRefreshRef.current);
      autoRefreshRef.current = null;
    }

    if (autoRefreshInterval > 0) {
      autoRefreshRef.current = setInterval(() => {
        loadAuditData();
      }, autoRefreshInterval);
    }

    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [autoRefreshInterval]);

  // ===== KEYBOARD SHORTCUTS EFFECT =====
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+E: Export CSV
      if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        if (!exporting && filteredRecords.length > 0) exportToCSV();
      }
      // Ctrl+P: Export PDF
      if (e.ctrlKey && e.key === 'p') {
        e.preventDefault();
        if (!exportingPdf && filteredRecords.length > 0) exportToPDF();
      }
      // Ctrl+F: Focus search
      if (e.ctrlKey && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      // Ctrl+R: Refresh data
      if (e.ctrlKey && e.key === 'r') {
        e.preventDefault();
        if (!loading) loadAuditData();
      }
      // Escape: Close expanded user / Clear filters
      if (e.key === 'Escape') {
        if (expandedUserId) {
          setExpandedUserId(null);
          setUserDetails(null);
        } else if (showAdvancedFilters) {
          setShowAdvancedFilters(false);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [exporting, exportingPdf, loading, filteredRecords.length, expandedUserId, showAdvancedFilters]);

  const loadAuditData = async () => {
    try {
      setLoading(true);
      setError('');

      const sessionHeaders = await ensureSessionHeaders();
      const response = await fetch('/api/admin/master-audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...sessionHeaders
        },
        body: JSON.stringify({ operation: 'load_audit_data' })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load audit data');
      }

      const { summaryData, auditData, __countryData } = result.data;

      if (summaryData) {
        setSummary({
          totalUsers: summaryData.total_users || 0,
          totalUSDTInSystem: parseFloat(summaryData.total_usdt_in_system || 0),
          totalSharesIssued: parseFloat(summaryData.total_shares_issued || 0),
          totalCommissionsPaid: parseFloat(summaryData.total_commissions_paid || 0),
          totalEscrowBalance: parseFloat(summaryData.total_escrow_balance || 0),
          totalAvailableUSDT: parseFloat(summaryData.total_available_usdt || 0),
        });
      }

      console.log(`✅ Loaded ${auditData?.length || 0} audit records`);
      setRecords(auditData || []);
      setFilteredRecords(auditData || []);
    } catch (err) {
      console.error('Error loading audit data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load audit data');
    } finally {
      setLoading(false);
    }
  };

  // OWNER-ONLY "Delete Account" (PII scrub): removes personal data so the
  // original email/username can be reused. ALL financial records are preserved.
  // Eligible only for suspended OR quarantined accounts (gated server-side too).
  const handleScrubDelete = async (record: AuditRecord) => {
    if (!isOwner) { alert('Only Johan, JP, or Ruvan can delete accounts.'); return; }
    if (!isDeletable(record)) { alert('Only suspended or quarantined accounts can be deleted.'); return; }
    const label = record.user_name || record.user_email || `User ${record.user_id}`;
    const ok = window.confirm(
      `Permanently DELETE the personal data for "${label}"?\n\n` +
      '• REMOVES email, username, phone, ID number, name & address\n' +
      '• FREES the original email for re-registration\n' +
      '• PRESERVES all financial records (shares, commissions, withdrawals, payments, referrals)\n\n' +
      'This action cannot be undone.'
    );
    if (!ok) return;
    setDeletingUserId(record.user_id);
    try {
      const response = await fetch('/api/admin/user-management', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
        body: JSON.stringify({ operation: 'scrub_delete_user', data: { userId: record.user_id } })
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || result.error || 'Failed to delete account');
      }
      alert(`🗑️ Account "${label}" deleted.\n\nPersonal data removed and the original email is now free for re-registration.\nAll financial records were preserved.`);
      await loadAuditData();
    } catch (err) {
      console.error('Error deleting account:', err);
      alert('Failed to delete account: ' + (err instanceof Error ? err.message : 'unknown error'));
    } finally {
      setDeletingUserId(null);
    }
  };

  const loadGoldDiggersData = async () => {
    try {
      setGoldDiggersLoading(true);
      const sessionHeaders = await ensureSessionHeaders();
      const response = await fetch('/api/admin/master-audit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...sessionHeaders },
        body: JSON.stringify({ operation: 'load_gold_diggers_winners' })
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.error || 'Failed to load');
      setGoldDiggersData(result.data || []);
    } catch (err) {
      console.error('Error loading gold diggers data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load gold diggers data');
    } finally {
      setGoldDiggersLoading(false);
    }
  };

  const loadUserDetails = async (userId: number) => {
    try {
      setDetailsLoading(true);

      const sessionHeaders = await ensureSessionHeaders();
      const response = await fetch('/api/admin/master-audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...sessionHeaders
        },
        body: JSON.stringify({ operation: 'load_user_details', data: { userId } })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Failed to load user details');
      }

      setUserDetails({
        purchases: result.data.purchases || [],
        commissions: result.data.commissions || [],
        conversions: result.data.conversions || [],
        transfers: result.data.transfers || [],
      });
    } catch (err) {
      console.error('Error loading user details:', err);
      setError(err instanceof Error ? err.message : 'Failed to load user details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const filterRecords = () => {
    let filtered = records;

    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();

      // Relevance-ranked filter: a record's score is the strongest match it produces.
      // Exact match on a primary identity field outranks a prefix match outranks a
      // substring match. Peripheral fields (phone, id-as-substring) are ranked lowest.
      // Inclusion semantics are unchanged: any record that previously matched (i.e.,
      // included(term) on any field) still produces a positive score and is included.
      const scored = filtered
        .map((r) => {
          const id = r.user_id.toString().toLowerCase();
          const email = (r.user_email || '').toLowerCase().trim();
          const name = (r.user_name || '').toLowerCase().trim();
          const username = (r.user_username || '').toLowerCase().trim();
          const phone = (r.user_phone || '').toLowerCase().trim();
          const kycName = (r.kyc_full_name || '').toLowerCase().trim();

          let score = 0;
          // Tier 1: exact equality on a primary identity field.
          if (id === term || email === term || name === term || username === term || kycName === term) {
            score = Math.max(score, 1000);
          }
          // Tier 2: prefix match on a primary identity field.
          if (name.startsWith(term) || username.startsWith(term) || email.startsWith(term) || kycName.startsWith(term) || id.startsWith(term)) {
            score = Math.max(score, 500);
          }
          // Tier 3: substring match on a primary identity field.
          if (name.includes(term) || username.includes(term) || email.includes(term) || kycName.includes(term)) {
            score = Math.max(score, 200);
          }
          // Tier 4: substring match on user_id (digit subsequence).
          if (id.includes(term)) {
            score = Math.max(score, 100);
          }
          // Tier 5: substring match on phone (peripheral identifier).
          if (phone.includes(term)) {
            score = Math.max(score, 50);
          }

          return { record: r, score };
        })
        .filter((s) => s.score > 0);

      // Primary sort: relevance score DESC. Secondary: user_id ASC for deterministic order.
      scored.sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return a.record.user_id - b.record.user_id;
      });

      filtered = scored.map((s) => s.record);
    }

    setFilteredRecords(filtered);
  };

  // Vulnerability detection helper
  const getVulnerabilityFlags = useCallback((record: AuditRecord): string[] => {
    const flags: string[] = [];
    // Financial discrepancy: shares don't match expected from purchases
    const expectedShares = (record.total_purchased_usdt || 0) / 25; // $25 per share
    const actualShares = record.total_shares_integer || 0;
    if (actualShares > 0 && expectedShares > 0 && Math.abs(actualShares - expectedShares) > expectedShares * 0.3) {
      flags.push('Share/Payment mismatch');
    }
    // Negative balance
    if ((record.available_balance_usdt || 0) < -1) {
      flags.push('Negative balance');
    }
    // High commission relative to purchases
    if ((record.commission_earned_usdt || 0) > (record.total_purchased_usdt || 0) * 2 && (record.commission_earned_usdt || 0) > 100) {
      flags.push('Unusual commission ratio');
    }
    // No KYC but has significant activity
    if (record.kyc_status === 'none' && (record.total_purchased_usdt || 0) > 100) {
      flags.push('No KYC with purchases');
    }
    // KYC rejected but still active
    if (record.kyc_status === 'rejected' && (record.total_shares_integer || 0) > 0) {
      flags.push('KYC rejected - has shares');
    }
    return flags;
  }, []);

  // Client-side filtering function (fallback if RPC doesn't exist)
  const applyClientSideFilters = (data: AuditRecord[]): AuditRecord[] => {
    let filtered = [...data];

    if (filters.email) {
      const emailFilter = filters.email.toLowerCase().trim();
      filtered = filtered.filter(r => r.user_email?.toLowerCase().includes(emailFilter));
    }
    if (filters.userId) {
      const userIdFilter = parseInt(filters.userId);
      filtered = filtered.filter(r => r.user_id === userIdFilter);
    }
    if (filters.minAmount) {
      const minAmount = parseFloat(filters.minAmount);
      filtered = filtered.filter(r => (r.total_purchased_usdt || 0) >= minAmount);
    }
    if (filters.maxAmount) {
      const maxAmount = parseFloat(filters.maxAmount);
      filtered = filtered.filter(r => (r.total_purchased_usdt || 0) <= maxAmount);
    }
    // Country filter
    if (filters.country) {
      const countryFilter = filters.country.toLowerCase().trim();
      filtered = filtered.filter(r =>
        (r.kyc_country_code || '').toLowerCase() === countryFilter ||
        (r.kyc_country_name || '').toLowerCase().includes(countryFilter)
      );
    }
    // KYC Status filter
    if (filters.kycStatus) {
      filtered = filtered.filter(r => (r.kyc_status || 'none') === filters.kycStatus);
    }
    // Vulnerability filter
    if (filters.vulnerabilityFilter === 'flagged') {
      filtered = filtered.filter(r => getVulnerabilityFlags(r).length > 0);
    } else if (filters.vulnerabilityFilter === 'clean') {
      filtered = filtered.filter(r => getVulnerabilityFlags(r).length === 0);
    }

    return filtered;
  };

  const applyAdvancedFilters = async () => {
    // Check if any filters are active
    const hasActiveFilters = Object.values(filters).some(v => v !== '');

    if (!hasActiveFilters) {
      setFilteredRecords(records);
      return;
    }

    try {
      setFilterLoading(true);

      const sessionHeaders = await ensureSessionHeaders();
      const response = await fetch('/api/admin/master-audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...sessionHeaders
        },
        body: JSON.stringify({
          operation: 'apply_filters',
          data: {
            email: filters.email || undefined,
            userId: filters.userId || undefined,
            minAmount: filters.minAmount || undefined,
            maxAmount: filters.maxAmount || undefined,
            transactionType: filters.transactionType || undefined,
            startDate: filters.startDate || undefined,
            endDate: filters.endDate || undefined
          }
        })
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        console.warn('API filter failed, using client-side filtering');
        const clientFiltered = applyClientSideFilters(records);
        setFilteredRecords(clientFiltered);
        return;
      }

      // Check if API returned fallback indicator
      if (result.data?.fallback) {
        console.warn('RPC filter_audit_records not available, using client-side filtering');
        const clientFiltered = applyClientSideFilters(records);
        setFilteredRecords(clientFiltered);
        return;
      }

      setFilteredRecords(result.data || []);
    } catch (err) {
      console.error('Error applying filters:', err);
      // Final fallback: use client-side filtering
      const clientFiltered = applyClientSideFilters(records);
      setFilteredRecords(clientFiltered);
    } finally {
      setFilterLoading(false);
    }
  };

  const clearAllFilters = () => {
    setFilters({
      email: '',
      userId: '',
      minAmount: '',
      maxAmount: '',
      transactionType: '',
      startDate: '',
      endDate: '',
      country: '',
      kycStatus: '',
      vulnerabilityFilter: ''
    });
    setSearchTerm('');
    setFilteredRecords(records);
  };

  const getActiveFilterCount = (): number => {
    return Object.values(filters).filter(v => v !== '').length;
  };

  const toggleUserExpand = (userId: number) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      setUserDetails(null);
    } else {
      setExpandedUserId(userId);
      loadUserDetails(userId);
    }
  };

  const loadTransactionProof = async (transactionReference: string, _paymentMethod: string) => {
    try {
      setProofLoading(true);
      setError('');

      const sessionHeaders = await ensureSessionHeaders();
      const response = await fetch('/api/admin/master-audit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...sessionHeaders
        },
        body: JSON.stringify({
          operation: 'load_transaction_proof',
          data: { transactionReference }
        })
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        console.error('Error loading proof:', result.error);
        setError('Transaction proof not found');
        return;
      }

      setSelectedProof(result.data);
      setProofModalOpen(true);
    } catch (err) {
      console.error('Error loading transaction proof:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transaction proof');
    } finally {
      setProofLoading(false);
    }
  };

  // Client-side CSV generation function (fallback/alternative)
  const generateCSVFromData = (data: AuditRecord[]): string => {
    const headers = [
      'ID', 'Username', 'Phone', 'Email', 'Name', 'Country', 'KYC Status',
      'USD Purchase Crypto', 'USD Purchase ZAR', 'Total Purchased (USDT)',
      'Shares Purchased', 'Shares from Comm', 'Bonus Shares', 'Total Shares',
      'Comm Earned (USDT)', 'Comm Converted (USDT)', 'Comm Withdrawals',
      'Admin Adjustments', 'Available Balance (USDT)'
    ];

    const csvRows = [headers.join(',')];
    for (const record of data) {
      const row = [
        record.user_id || '',
        `"${(record.user_username || '').replace(/"/g, '""')}"`,
        `"${(record.user_phone || record.kyc_phone || '').replace(/"/g, '""')}"`,
        `"${(record.user_email || '').replace(/"/g, '""')}"`,
        `"${(record.user_name || '').replace(/"/g, '""')}"`,
        `"${(record.user_country || record.kyc_country_name || '').replace(/"/g, '""')}"`,
        `"${(record.kyc_status || 'none').replace(/"/g, '""')}"`,
        (record.usd_purchase_crypto || 0).toFixed(2),
        (record.usd_purchase_zar || 0).toFixed(2),
        (record.total_purchased_usdt || 0).toFixed(2),
        (record.shares_purchased_monetary || 0).toFixed(2),
        (record.shares_from_commission || 0).toFixed(2),
        (record.bonus_shares_15_percent || 0).toFixed(2),
        (record.total_shares_decimal || 0).toFixed(2),
        (record.commission_earned_usdt || 0).toFixed(2),
        (record.commission_converted_to_shares || 0).toFixed(2),
        (record.commission_withdrawals || 0).toFixed(2),
        (record.admin_adjustments || 0).toFixed(2),
        (record.available_balance_usdt || 0).toFixed(2)
      ];
      csvRows.push(row.join(','));
    }
    return csvRows.join('\n');
  };

  const downloadCSV = (csvContent: string, filename: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const exportToCSV = async () => {
    try {
      setExporting(true);
      setError('');

      // Try server-side export first (handles large datasets better)
      try {
        const response = await fetch('/api/admin/export-financial-audit', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
          body: JSON.stringify({
            filters: {
              email: filters.email || undefined,
              userId: filters.userId || undefined,
              minAmount: filters.minAmount || undefined,
              maxAmount: filters.maxAmount || undefined,
              transactionType: filters.transactionType || undefined,
              startDate: filters.startDate || undefined,
              endDate: filters.endDate || undefined
            },
            searchTerm: searchTerm || undefined
          }),
        });

        if (response.ok) {
          const csvData = await response.text();
          downloadCSV(csvData, `financial-audit-${new Date().toISOString().split('T')[0]}.csv`);
          setSuccess('CSV exported successfully!');
          setTimeout(() => setSuccess(''), 3000);
          return;
        }

        // If server-side fails, fall through to client-side export
        console.warn('Server-side export failed, falling back to client-side export');
      } catch (serverError) {
        console.warn('Server-side export error, using client-side fallback:', serverError);
      }

      // Fallback: Client-side CSV generation using currently filtered data
      const dataToExport = filteredRecords.length > 0 ? filteredRecords : records;
      if (dataToExport.length === 0) {
        throw new Error('No data to export');
      }

      const csvContent = generateCSVFromData(dataToExport);
      downloadCSV(csvContent, `financial-audit-${new Date().toISOString().split('T')[0]}.csv`);

      setSuccess(`CSV exported successfully! (${dataToExport.length} records)`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      setError(err instanceof Error ? err.message : 'Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  // ===== PDF EXPORT FUNCTION =====
  const exportToPDF = async () => {
    try {
      setExportingPdf(true);
      setError('');

      const dataToExport = filteredRecords.length > 0 ? filteredRecords : records;
      if (dataToExport.length === 0) {
        throw new Error('No data to export');
      }

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      let yPos = 20;

      // Company Header
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 215, 0);
      doc.text('AUREUS ALLIANCE HOLDINGS (PTY) LTD', pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      doc.setFontSize(14);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Master Financial Audit Report', pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Report metadata
      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;
      doc.text(`Total Records: ${dataToExport.length}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Horizontal line
      doc.setDrawColor(255, 215, 0);
      doc.setLineWidth(1);
      doc.line(20, yPos, pageWidth - 20, yPos);
      yPos += 15;

      // Summary Statistics
      if (summary) {
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('EXECUTIVE SUMMARY', 20, yPos);
        yPos += 10;

        const summaryData = [
          ['Total Users', summary.totalUsers.toLocaleString()],
          ['Total USDT Deposited', `$${summary.totalUSDTInSystem.toFixed(2)}`],
          ['Total Shares Issued', summary.totalSharesIssued.toLocaleString()],
          ['Total Commissions Paid', `$${summary.totalCommissionsPaid.toFixed(2)}`],
          ['Total Escrow Balance', `$${summary.totalEscrowBalance.toFixed(2)}`],
          ['Total Available USDT', `$${summary.totalAvailableUSDT.toFixed(2)}`]
        ];

        autoTable(doc, {
          startY: yPos,
          head: [['Metric', 'Value']],
          body: summaryData,
          theme: 'grid',
          headStyles: { fillColor: [255, 215, 0], textColor: [0, 0, 0] },
          margin: { left: 20, right: 20 }
        });

        yPos = (doc as any).lastAutoTable.finalY + 15;
      }

      // User Data Table
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('USER FINANCIAL DATA', 20, yPos);
      yPos += 10;

      const tableData = dataToExport.slice(0, 100).map(r => [
        r.user_id.toString(),
        (r.user_username || '').substring(0, 15),
        (r.user_email || '').substring(0, 25),
        (r.user_name || '').substring(0, 20),
        `$${(r.usd_purchase_crypto || 0).toFixed(2)}`,
        `$${(r.usd_purchase_zar || 0).toFixed(2)}`,
        `$${(r.total_purchased_usdt || 0).toFixed(2)}`,
        (r.shares_purchased_monetary || 0).toFixed(2),
        (r.shares_from_commission || 0).toFixed(2),
        (r.bonus_shares_15_percent || 0).toFixed(2),
        (r.total_shares_decimal || 0).toFixed(2),
        `$${(r.commission_earned_usdt || 0).toFixed(2)}`,
        `$${(r.commission_withdrawals || 0).toFixed(2)}`,
        `$${(r.available_balance_usdt || 0).toFixed(2)}`
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['ID', 'Username', 'Email', 'Name', 'Crypto', 'ZAR', 'Total Purch', 'Shares', 'Comm Shares', 'Bonus', 'Tot Shares', 'Comm USDT', 'Withdrawals', 'Available']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [255, 215, 0], textColor: [0, 0, 0], fontSize: 6 },
        bodyStyles: { fontSize: 5 },
        margin: { left: 10, right: 10 }
      });

      // Footer
      const pageCount = doc.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 20, doc.internal.pageSize.height - 10, { align: 'right' });
        doc.text('Aureus Alliance Holdings - Confidential', 20, doc.internal.pageSize.height - 10);
      }

      // Save
      doc.save(`master-audit-report-${new Date().toISOString().split('T')[0]}.pdf`);
      setSuccess('PDF exported successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      setError(err instanceof Error ? err.message : 'Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  // ===== EXCEL EXPORT FUNCTION =====
  const exportToExcel = async () => {
    try {
      setExportingExcel(true);
      setError('');

      const dataToExport = filteredRecords.length > 0 ? filteredRecords : records;
      if (dataToExport.length === 0) {
        throw new Error('No data to export');
      }

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Summary sheet
      if (summary) {
        const summarySheet = XLSX.utils.aoa_to_sheet([
          ['Master Audit Summary', ''],
          ['Generated', new Date().toLocaleString()],
          [''],
          ['Metric', 'Value'],
          ['Total Users', summary.totalUsers],
          ['Total USDT Deposited', summary.totalUSDTInSystem],
          ['Total Shares Issued', summary.totalSharesIssued],
          ['Total Commissions Paid', summary.totalCommissionsPaid],
          ['Total Escrow Balance', summary.totalEscrowBalance],
          ['Total Available USDT', summary.totalAvailableUSDT]
        ]);
        XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');
      }

      // User Data sheet
      const userData = dataToExport.map(r => ({
        'ID': r.user_id,
        'Username': r.user_username || '',
        'Phone': r.user_phone || r.kyc_phone || '',
        'Email': r.user_email || '',
        'Name': r.user_name || '',
        'Country': r.user_country || r.kyc_country_name || '',
        'KYC Status': r.kyc_status || 'none',
        'USD Purchase Crypto': Number((r.usd_purchase_crypto || 0).toFixed(2)),
        'USD Purchase ZAR': Number((r.usd_purchase_zar || 0).toFixed(2)),
        'Total Purchased (USDT)': Number((r.total_purchased_usdt || 0).toFixed(2)),
        'Shares Purchased': Number((r.shares_purchased_monetary || 0).toFixed(2)),
        'Shares from Comm': Number((r.shares_from_commission || 0).toFixed(2)),
        'Bonus Shares': Number((r.bonus_shares_15_percent || 0).toFixed(2)),
        'Total Shares': Number((r.total_shares_decimal || 0).toFixed(2)),
        'Comm Earned (USDT)': Number((r.commission_earned_usdt || 0).toFixed(2)),
        'Comm Converted (USDT)': Number((r.commission_converted_to_shares || 0).toFixed(2)),
        'Comm Withdrawals': Number((r.commission_withdrawals || 0).toFixed(2)),
        'Admin Adjustments': Number((r.admin_adjustments || 0).toFixed(2)),
        'Available Balance (USDT)': Number((r.available_balance_usdt || 0).toFixed(2)),
        'Payment Methods': r.payment_methods_summary || ''
      }));
      const userSheet = XLSX.utils.json_to_sheet(userData);
      XLSX.utils.book_append_sheet(wb, userSheet, 'User Data');

      // Save file
      XLSX.writeFile(wb, `master-audit-${new Date().toISOString().split('T')[0]}.xlsx`);
      setSuccess(`Excel exported successfully! (${dataToExport.length} records)`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error exporting Excel:', err);
      setError(err instanceof Error ? err.message : 'Failed to export Excel');
    } finally {
      setExportingExcel(false);
    }
  };

  // ===== COLUMN SORTING FUNCTION =====
  const handleSort = (column: keyof AuditRecord) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Get sorted records
  const getSortedRecords = useCallback(() => {
    if (!sortColumn) return filteredRecords;

    return [...filteredRecords].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison = 0;
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredRecords, sortColumn, sortDirection]);

  // ===== DATE PRESET HANDLER =====
  const applyDatePreset = (preset: typeof DATE_PRESETS[0]) => {
    const { start, end } = preset.getValue();
    setFilters(prev => ({ ...prev, startDate: start, endDate: end }));
  };

  // ===== PRINT FUNCTION =====
  const handlePrint = () => {
    window.print();
  };

  // ===== CHART DATA PREPARATION =====
  const getChartData = useCallback(() => {
    const sortedByPurchase = [...filteredRecords]
      .sort((a, b) => (b.total_purchased_usdt || 0) - (a.total_purchased_usdt || 0))
      .slice(0, 10);

    return sortedByPurchase.map(r => ({
      name: r.user_email?.split('@')[0] || `User ${r.user_id}`,
      purchased: r.total_purchased_usdt || 0,
      shares: r.total_shares_decimal || 0,
      commission: r.commission_earned_usdt || 0
    }));
  }, [filteredRecords]);

  const getPieChartData = useCallback(() => {
    if (!summary) return [];
    return [
      { name: 'USDT in System', value: summary.totalUSDTInSystem },
      { name: 'Commissions Paid', value: summary.totalCommissionsPaid },
      { name: 'Escrow Balance', value: summary.totalEscrowBalance },
      { name: 'Available USDT', value: summary.totalAvailableUSDT }
    ];
  }, [summary]);

  const getFieldsForTable = (table: string): string[] => {
    const fieldMap: Record<string, string[]> = {
      'commission_transactions': [
        'referred_user_id', 'share_purchase_amount', 'usdt_commission',
        'share_commission', 'status', 'payment_date'
      ],
      'aureus_share_purchases': [
        'user_id', 'shares_purchased', 'amount_paid', 'payment_method',
        'payment_date', 'status'
      ],
      'commission_conversions': [
        'user_id', 'usdt_amount', 'shares_received', 'conversion_date', 'status'
      ],
      'share_transfers': [
        'from_user_id', 'to_user_id', 'shares_transferred', 'transfer_date', 'status'
      ]
    };
    return fieldMap[table] || [];
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
      {/* Print Styles */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media print {
          body { background: white !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .print-table { border: 1px solid #000 !important; }
          .print-table th, .print-table td { border: 1px solid #ccc !important; padding: 8px !important; color: #000 !important; background: #fff !important; }
          .print-table th { background: #f0f0f0 !important; font-weight: bold !important; }
          button, select, input { display: none !important; }
          [style*="backgroundColor: #1F2937"] { background: #f8f8f8 !important; }
          h1, h3, p, span, td, th { color: #000 !important; }
          @page { margin: 1cm; size: landscape; }
        }
      `}} />

      {/* Personal Activity Report — visible only when admin_users.personal_activity_report_enabled = true (server-enforced) */}
      <PersonalActivityReport currentUser={_currentUser} />

      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: '600', marginBottom: '8px', color: '#FFFFFF' }}>
          📊 Master Audit Dashboard - Enhanced
        </h1>
        <p style={{ color: '#ffffff', fontSize: '14px' }}>
          Comprehensive financial reporting with detailed transaction drill-down
        </p>
      </div>

      {/* Main View Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button onClick={() => setMainView('audit')} style={{ padding: '10px 24px', backgroundColor: mainView === 'audit' ? '#3B82F6' : '#374151', color: '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: mainView === 'audit' ? '700' : '400', fontSize: '14px' }}>
          📊 Financial Audit
        </button>
        <button onClick={() => { setMainView('golddiggers'); if (goldDiggersData.length === 0) loadGoldDiggersData(); }} style={{ padding: '10px 24px', backgroundColor: mainView === 'golddiggers' ? '#F59E0B' : '#374151', color: mainView === 'golddiggers' ? '#000' : '#FFFFFF', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: mainView === 'golddiggers' ? '700' : '400', fontSize: '14px' }}>
          🏆 Gold Diggers Winners
        </button>
      </div>

      {error && (
        <div style={{ backgroundColor: '#FEE2E2', color: '#991B1B', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ backgroundColor: '#DCFCE7', color: '#166534', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px' }}>
          {success}
        </div>
      )}

      {/* ===== GOLD DIGGERS WINNERS VIEW ===== */}
      {mainView === 'golddiggers' && (
        <div>
          <h2 style={{ color: '#F59E0B', fontSize: '22px', fontWeight: '700', marginBottom: '16px' }}>🏆 Gold Diggers Competition Winners</h2>
          {goldDiggersLoading ? (
            <div style={{ color: '#FFFFFF', padding: '40px', textAlign: 'center' }}>⏳ Loading competition winners...</div>
          ) : goldDiggersData.length === 0 ? (
            <div style={{ color: '#9CA3AF', padding: '40px', textAlign: 'center' }}>No competition winners found.</div>
          ) : (
            <>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '24px' }}>
                <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
                  <h3 style={{ color: '#9CA3AF', fontSize: '12px', marginBottom: '4px' }}>Total Prizes Awarded</h3>
                  <p style={{ fontSize: '22px', fontWeight: '700', color: '#F59E0B' }}>${goldDiggersData.reduce((s: number, w: any) => s + parseFloat(w.prize_amount || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
                  <h3 style={{ color: '#9CA3AF', fontSize: '12px', marginBottom: '4px' }}>Total Winners</h3>
                  <p style={{ fontSize: '22px', fontWeight: '700', color: '#FFFFFF' }}>{goldDiggersData.length}</p>
                </div>
                <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
                  <h3 style={{ color: '#9CA3AF', fontSize: '12px', marginBottom: '4px' }}>Unique Winners</h3>
                  <p style={{ fontSize: '22px', fontWeight: '700', color: '#FFFFFF' }}>{new Set(goldDiggersData.map((w: any) => w.user_id)).size}</p>
                </div>
                <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
                  <h3 style={{ color: '#9CA3AF', fontSize: '12px', marginBottom: '4px' }}>Phases Completed</h3>
                  <p style={{ fontSize: '22px', fontWeight: '700', color: '#FFFFFF' }}>{new Set(goldDiggersData.map((w: any) => w.phase_id)).size}</p>
                </div>
              </div>
              {/* Winners table */}
              <div style={{ overflowX: 'auto', border: '1px solid #374151', borderRadius: '8px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#1F2937' }}>
                      <th style={{ padding: '10px 12px', textAlign: 'left', color: '#F59E0B', fontWeight: '700', fontSize: '12px' }}>Phase</th>
                      <th style={{ padding: '10px 12px', textAlign: 'center', color: '#F59E0B', fontWeight: '700', fontSize: '12px' }}>Rank</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', color: '#FFFFFF', fontWeight: '600', fontSize: '12px' }}>User ID</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', color: '#FFFFFF', fontWeight: '600', fontSize: '12px' }}>Username</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', color: '#10B981', fontWeight: '700', fontSize: '12px' }}>Prize Amount</th>
                      <th style={{ padding: '10px 12px', textAlign: 'right', color: '#FFFFFF', fontWeight: '600', fontSize: '12px' }}>Direct Purchases</th>
                      <th style={{ padding: '10px 12px', textAlign: 'left', color: '#FFFFFF', fontWeight: '600', fontSize: '12px' }}>Awarded Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {goldDiggersData.map((w: any, i: number) => {
                      const medal = w.rank === 1 ? '🥇' : w.rank === 2 ? '🥈' : w.rank === 3 ? '🥉' : '';
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid #374151', backgroundColor: w.rank <= 3 ? 'rgba(245,158,11,0.06)' : 'transparent' }}>
                          <td style={{ padding: '8px 12px', color: '#F59E0B', fontWeight: '600', fontSize: '13px' }}>Phase {w.phase_id}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center', color: '#FFFFFF', fontSize: '13px' }}>{medal} {w.rank}</td>
                          <td style={{ padding: '8px 12px', color: '#9CA3AF', fontSize: '12px' }}>{w.user_id}</td>
                          <td style={{ padding: '8px 12px', color: '#60A5FA', fontSize: '13px' }}>{w.username}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: '#10B981', fontWeight: '700', fontSize: '13px' }}>${parseFloat(w.prize_amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'right', color: '#FFFFFF', fontSize: '12px' }}>${parseFloat(w.total_direct_purchases || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td style={{ padding: '8px 12px', color: '#9CA3AF', fontSize: '12px' }}>{w.awarded_at ? new Date(w.awarded_at).toLocaleDateString() : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== FINANCIAL AUDIT VIEW ===== */}
      {mainView === 'audit' && <>

      {/* Summary Statistics */}
      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '32px' }}>
          <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
            <h3 style={{ color: '#ffffff', fontSize: '14px', marginBottom: '8px' }}>Total Users</h3>
            <p style={{ fontSize: '24px', fontWeight: '600', color: '#FFFFFF' }}>{summary.totalUsers.toLocaleString()}</p>
          </div>
          <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
            <h3 style={{ color: '#ffffff', fontSize: '14px', marginBottom: '8px' }}>Total USDT Deposited</h3>
            <p style={{ fontSize: '24px', fontWeight: '600', color: '#FFFFFF' }}>${summary.totalUSDTInSystem.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
            <h3 style={{ color: '#ffffff', fontSize: '14px', marginBottom: '8px' }}>Total Shares Issued</h3>
            <p style={{ fontSize: '24px', fontWeight: '600', color: '#FFFFFF' }}>{summary.totalSharesIssued.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
            <h3 style={{ color: '#ffffff', fontSize: '14px', marginBottom: '8px' }}>Total Commissions Paid</h3>
            <p style={{ fontSize: '24px', fontWeight: '600', color: '#FFFFFF' }}>${summary.totalCommissionsPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
            <h3 style={{ color: '#ffffff', fontSize: '14px', marginBottom: '8px' }}>Total Escrow Balance</h3>
            <p style={{ fontSize: '24px', fontWeight: '600', color: '#FFFFFF' }}>${summary.totalEscrowBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', padding: '16px' }}>
            <h3 style={{ color: '#ffffff', fontSize: '14px', marginBottom: '8px' }}>Total Available USDT</h3>
            <p style={{ fontSize: '24px', fontWeight: '600', color: '#FFFFFF' }}>${summary.totalAvailableUSDT.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}

      {/* Controls - Row 1: Search and Main Actions */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search by ID, Username, Email, Name, or Phone (Ctrl+F)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: '300px', padding: '8px 12px', backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '6px', color: '#FFFFFF' }}
        />
        <button
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          style={{
            padding: '8px 16px',
            backgroundColor: showAdvancedFilters ? '#3B82F6' : '#374151',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          🔍 Filters
          {getActiveFilterCount() > 0 && (
            <span style={{
              backgroundColor: '#F59E0B',
              color: '#000000',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '14px',
              fontWeight: '600'
            }}>
              {getActiveFilterCount()}
            </span>
          )}
        </button>
        <button
          onClick={() => setShowCharts(!showCharts)}
          style={{
            padding: '8px 16px',
            backgroundColor: showCharts ? '#8B5CF6' : '#374151',
            color: '#FFFFFF',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          📊 Charts
        </button>
        <button
          onClick={loadAuditData}
          disabled={loading}
          style={{ padding: '8px 16px', backgroundColor: '#374151', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', opacity: loading ? 0.6 : 1 }}
          title="Ctrl+R"
        >
          {loading ? '⏳ Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Controls - Row 2: Export Options and Auto-Refresh */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Export Buttons */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', backgroundColor: '#1F2937', padding: '6px 12px', borderRadius: '6px', border: '1px solid #374151' }}>
          <span style={{ color: '#ffffff', fontSize: '13px' }}>Export:</span>
          <button
            onClick={exportToCSV}
            disabled={exporting || filteredRecords.length === 0}
            style={{ padding: '6px 12px', backgroundColor: '#10B981', color: '#FFFFFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', opacity: (exporting || filteredRecords.length === 0) ? 0.6 : 1 }}
            title="Ctrl+E"
          >
            {exporting ? '⏳' : '📄'} CSV
          </button>
          <button
            onClick={exportToExcel}
            disabled={exportingExcel || filteredRecords.length === 0}
            style={{ padding: '6px 12px', backgroundColor: '#3B82F6', color: '#FFFFFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', opacity: (exportingExcel || filteredRecords.length === 0) ? 0.6 : 1 }}
          >
            {exportingExcel ? '⏳' : '📊'} Excel
          </button>
          <button
            onClick={exportToPDF}
            disabled={exportingPdf || filteredRecords.length === 0}
            style={{ padding: '6px 12px', backgroundColor: '#EF4444', color: '#FFFFFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', opacity: (exportingPdf || filteredRecords.length === 0) ? 0.6 : 1 }}
            title="Ctrl+P"
          >
            {exportingPdf ? '⏳' : '📕'} PDF
          </button>
          <button
            onClick={handlePrint}
            style={{ padding: '6px 12px', backgroundColor: '#ffffff', color: '#FFFFFF', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}
          >
            🖨️ Print
          </button>
        </div>

        {/* Auto-Refresh Control */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', backgroundColor: '#1F2937', padding: '6px 12px', borderRadius: '6px', border: '1px solid #374151' }}>
          <span style={{ color: '#ffffff', fontSize: '13px' }}>Auto-Refresh:</span>
          <select
            value={autoRefreshInterval}
            onChange={(e) => setAutoRefreshInterval(Number(e.target.value))}
            style={{ padding: '4px 8px', backgroundColor: '#111827', color: '#FFFFFF', border: '1px solid #374151', borderRadius: '4px', fontSize: '13px' }}
          >
            {REFRESH_INTERVALS.map(interval => (
              <option key={interval.value} value={interval.value}>{interval.label}</option>
            ))}
          </select>
          {autoRefreshInterval > 0 && (
            <span style={{ color: '#10B981', fontSize: '14px' }}>● Active</span>
          )}
        </div>

        {/* Records Count */}
        <div style={{ marginLeft: 'auto', color: '#ffffff', fontSize: '13px' }}>
          Showing <span style={{ color: '#FFFFFF', fontWeight: '600' }}>{filteredRecords.length}</span> of <span style={{ color: '#FFFFFF' }}>{records.length}</span> users
        </div>
      </div>

      {/* Keyboard Shortcuts Help */}
      <div style={{ marginBottom: '16px', padding: '8px 12px', backgroundColor: '#1F2937', borderRadius: '6px', border: '1px solid #374151' }}>
        <span style={{ color: '#ffffff', fontSize: '13px' }}>
          ⌨️ Shortcuts: <span style={{ color: '#ffffff' }}>Ctrl+F</span> Search | <span style={{ color: '#ffffff' }}>Ctrl+R</span> Refresh | <span style={{ color: '#ffffff' }}>Ctrl+E</span> CSV | <span style={{ color: '#ffffff' }}>Ctrl+P</span> PDF | <span style={{ color: '#ffffff' }}>Esc</span> Close
        </span>
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div style={{
          backgroundColor: '#1F2937',
          border: '1px solid #374151',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: '600' }}>🔍 Advanced Filters</h3>
            <button
              onClick={clearAllFilters}
              style={{
                padding: '6px 12px',
                backgroundColor: '#EF4444',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Clear All
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            {/* Email Filter */}
            <div>
              <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '6px' }}>
                Email Address
              </label>
              <input
                type="text"
                placeholder="user@example.com"
                value={filters.email}
                onChange={(e) => setFilters({ ...filters, email: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#FFFFFF'
                }}
              />
            </div>

            {/* User ID Filter */}
            <div>
              <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '6px' }}>
                User ID
              </label>
              <input
                type="number"
                placeholder="123"
                value={filters.userId}
                onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#FFFFFF'
                }}
              />
            </div>

            {/* Transaction Type Filter */}
            <div>
              <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '6px' }}>
                Transaction Type
              </label>
              <select
                value={filters.transactionType}
                onChange={(e) => setFilters({ ...filters, transactionType: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#FFFFFF'
                }}
              >
                <option value="">All Types</option>
                <option value="purchases">Share Purchases</option>
                <option value="commissions">Commission Transactions</option>
                <option value="conversions">Commission Conversions</option>
                <option value="transfers">Share Transfers</option>
              </select>
            </div>

            {/* Min Amount Filter */}
            <div>
              <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '6px' }}>
                Min Amount (USDT)
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={filters.minAmount}
                onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#FFFFFF'
                }}
              />
            </div>

            {/* Max Amount Filter */}
            <div>
              <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '6px' }}>
                Max Amount (USDT)
              </label>
              <input
                type="number"
                step="0.01"
                placeholder="10000.00"
                value={filters.maxAmount}
                onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#FFFFFF'
                }}
              />
            </div>

            {/* Start Date Filter */}
            <div>
              <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '6px' }}>
                Start Date
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#FFFFFF'
                }}
              />
            </div>

            {/* End Date Filter */}
            <div>
              <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '6px' }}>
                End Date
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#FFFFFF'
                }}
              />
            </div>

            {/* Country Filter */}
            <div>
              <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '6px' }}>
                🌍 Country
              </label>
              <select
                value={filters.country}
                onChange={(e) => setFilters({ ...filters, country: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#FFFFFF'
                }}
              >
                <option value="">All Countries</option>
                {Array.from(new Set(records.map(r => r.kyc_country_name).filter(Boolean))).sort().map(country => (
                  <option key={country} value={country!.toLowerCase()}>{country}</option>
                ))}
              </select>
            </div>

            {/* KYC Status Filter */}
            <div>
              <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '6px' }}>
                🪪 KYC Status
              </label>
              <select
                value={filters.kycStatus}
                onChange={(e) => setFilters({ ...filters, kycStatus: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#FFFFFF'
                }}
              >
                <option value="">All Statuses</option>
                <option value="approved">✅ Approved</option>
                <option value="pending">⏳ Pending</option>
                <option value="rejected">❌ Rejected</option>
                <option value="none">⚠️ No KYC</option>
              </select>
            </div>

            {/* Vulnerability Filter */}
            <div>
              <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '6px' }}>
                🛡️ Vulnerability
              </label>
              <select
                value={filters.vulnerabilityFilter}
                onChange={(e) => setFilters({ ...filters, vulnerabilityFilter: e.target.value })}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  backgroundColor: '#111827',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  color: '#FFFFFF'
                }}
              >
                <option value="">All Records</option>
                <option value="flagged">🚩 Flagged Only</option>
                <option value="clean">✅ Clean Only</option>
              </select>
            </div>
          </div>

          {/* Date Range Quick Presets */}
          <div style={{ marginTop: '16px', borderTop: '1px solid #374151', paddingTop: '16px' }}>
            <label style={{ display: 'block', color: '#ffffff', fontSize: '14px', marginBottom: '8px' }}>
              📅 Quick Date Presets:
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.label}
                  onClick={() => applyDatePreset(preset)}
                  style={{
                    padding: '6px 12px',
                    backgroundColor: '#374151',
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {filterLoading && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#111827',
              borderRadius: '6px',
              textAlign: 'center',
              color: '#ffffff'
            }}>
              🔄 Applying filters...
            </div>
          )}

          {!filterLoading && filteredRecords.length === 0 && getActiveFilterCount() > 0 && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: '#FEE2E2',
              borderRadius: '6px',
              textAlign: 'center',
              color: '#991B1B'
            }}>
              ⚠️ No results found matching your filters
            </div>
          )}
        </div>
      )}

      {/* Charts Section */}
      {showCharts && (
        <div style={{ backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', padding: '20px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: '600' }}>📊 Data Visualization</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setChartType('pie')}
                style={{
                  padding: '6px 12px',
                  backgroundColor: chartType === 'pie' ? '#8B5CF6' : '#374151',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                🥧 Pie Chart
              </button>
              <button
                onClick={() => setChartType('bar')}
                style={{
                  padding: '6px 12px',
                  backgroundColor: chartType === 'bar' ? '#8B5CF6' : '#374151',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                📊 Bar Chart
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
            {/* Chart 1: System Distribution (Pie) or Top Users (Bar) */}
            <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '16px' }}>
              <h4 style={{ color: '#ffffff', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>
                {chartType === 'pie' ? 'System Fund Distribution' : 'Top 10 Users by Purchase'}
              </h4>
              <ResponsiveContainer width="100%" height={280}>
                {chartType === 'pie' ? (
                  <PieChart>
                    <Pie
                      data={getPieChartData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getPieChartData().map((_entry, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value: number) => `$${value.toFixed(2)}`} />
                    <Legend />
                  </PieChart>
                ) : (
                  <BarChart data={getChartData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="name" stroke="#9CA3AF" fontSize={10} />
                    <YAxis stroke="#9CA3AF" fontSize={10} />
                    <Tooltip formatter={(value: number) => `$${value.toFixed(2)}`} />
                    <Legend />
                    <Bar dataKey="purchased" name="Purchased (USDT)" fill="#F59E0B" />
                    <Bar dataKey="commission" name="Commission (USDT)" fill="#10B981" />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>

            {/* Chart 2: Shares Distribution */}
            <div style={{ backgroundColor: '#111827', borderRadius: '8px', padding: '16px' }}>
              <h4 style={{ color: '#ffffff', fontSize: '14px', marginBottom: '16px', textAlign: 'center' }}>
                Top 10 Users by Shares
              </h4>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={getChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9CA3AF" fontSize={10} />
                  <YAxis stroke="#9CA3AF" fontSize={10} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="shares" name="Total Shares" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Users Table with Expandable Rows and Sortable Columns */}
      <div style={{ overflowX: 'auto', backgroundColor: '#1F2937', border: '1px solid #374151', borderRadius: '8px', marginBottom: '16px' }} className="print-table">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#111827', borderBottom: '1px solid #374151' }}>
              <th style={{ padding: '8px 6px', textAlign: 'left', color: '#ffffff', fontWeight: '600', fontSize: '12px', width: '30px' }}>▼</th>
              <th onClick={() => handleSort('user_id')} style={{ padding: '8px 6px', textAlign: 'left', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                ID {sortColumn === 'user_id' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('user_username')} style={{ padding: '8px 6px', textAlign: 'left', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Username {sortColumn === 'user_username' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th style={{ padding: '8px 6px', textAlign: 'left', color: '#ffffff', fontWeight: '600', fontSize: '12px', whiteSpace: 'nowrap' }}>Phone</th>
              <th onClick={() => handleSort('user_email')} style={{ padding: '8px 6px', textAlign: 'left', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Email {sortColumn === 'user_email' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('user_name')} style={{ padding: '8px 6px', textAlign: 'left', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Name {sortColumn === 'user_name' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('user_country')} style={{ padding: '8px 6px', textAlign: 'left', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Country {sortColumn === 'user_country' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('kyc_status')} style={{ padding: '8px 6px', textAlign: 'center', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                KYC {sortColumn === 'kyc_status' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('usd_purchase_crypto')} style={{ padding: '8px 6px', textAlign: 'right', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                USD Purchase Crypto {sortColumn === 'usd_purchase_crypto' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('usd_purchase_zar')} style={{ padding: '8px 6px', textAlign: 'right', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                USD Purchase ZAR {sortColumn === 'usd_purchase_zar' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('total_purchased_usdt')} style={{ padding: '8px 6px', textAlign: 'right', color: '#F59E0B', fontWeight: '700', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Total Purchased USDT {sortColumn === 'total_purchased_usdt' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('shares_purchased_monetary')} style={{ padding: '8px 6px', textAlign: 'right', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Shares Purchased {sortColumn === 'shares_purchased_monetary' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('shares_from_commission')} style={{ padding: '8px 6px', textAlign: 'right', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Shares from Comm {sortColumn === 'shares_from_commission' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('bonus_shares_15_percent')} style={{ padding: '8px 6px', textAlign: 'right', color: '#FBBF24', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }} title="Bonus/gift shares (BOGO, Anniversary, tier & legacy bonuses). Already included in Total Shares.">
                🎁 Bonus Shares {sortColumn === 'bonus_shares_15_percent' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('total_shares_decimal')} style={{ padding: '8px 6px', textAlign: 'right', color: '#F59E0B', fontWeight: '700', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }} title="Net equity shares — already includes purchased + commission + bonus/gift shares.">
                Total Shares {sortColumn === 'total_shares_decimal' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('commission_earned_usdt')} style={{ padding: '8px 6px', textAlign: 'right', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Comm Earned USDT {sortColumn === 'commission_earned_usdt' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('commission_converted_to_shares')} style={{ padding: '8px 6px', textAlign: 'right', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Comm→Shares $ {sortColumn === 'commission_converted_to_shares' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('commission_withdrawals')} style={{ padding: '8px 6px', textAlign: 'right', color: '#ffffff', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Comm Withdrawals {sortColumn === 'commission_withdrawals' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('competition_prizes')} style={{ padding: '8px 6px', textAlign: 'right', color: '#F59E0B', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                🏆 Prizes {sortColumn === 'competition_prizes' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('manual_admin_additions')} style={{ padding: '8px 6px', textAlign: 'right', color: '#C084FC', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                📝 Manual {sortColumn === 'manual_admin_additions' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('admin_adjustments')} style={{ padding: '8px 6px', textAlign: 'right', color: '#A78BFA', fontWeight: '600', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Admin Adj. {sortColumn === 'admin_adjustments' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th onClick={() => handleSort('available_balance_usdt')} style={{ padding: '8px 6px', textAlign: 'right', color: '#10B981', fontWeight: '700', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                Available Balance {sortColumn === 'available_balance_usdt' && (sortDirection === 'asc' ? '↑' : '↓')}
              </th>
              <th style={{ padding: '8px 6px', textAlign: 'center', color: '#ffffff', fontWeight: '600', fontSize: '12px' }}>Flags</th>
              <th style={{ padding: '8px 6px', textAlign: 'center', color: '#ffffff', fontWeight: '600', fontSize: '12px' }}>Audit</th>
            </tr>
          </thead>
          <tbody>
            {getSortedRecords().map((record) => {
              const vulnFlags = getVulnerabilityFlags(record);
              return (
              <React.Fragment key={record.user_id}>
                <tr style={{ borderBottom: '1px solid #374151', cursor: 'pointer', backgroundColor: vulnFlags.length > 0 ? 'rgba(239,68,68,0.08)' : 'transparent' }} onClick={() => toggleUserExpand(record.user_id)}>
                  <td style={{ padding: '6px', color: '#FFFFFF', fontSize: '12px', textAlign: 'center' }}>
                    {expandedUserId === record.user_id ? '▼' : '▶'}
                  </td>
                  <td style={{ padding: '6px', color: '#FFFFFF', fontSize: '12px' }}>{record.user_id}</td>
                  <td style={{ padding: '6px', color: '#60A5FA', fontSize: '12px' }}>{record.user_username || '-'}</td>
                  <td style={{ padding: '6px', color: '#D1D5DB', fontSize: '11px', whiteSpace: 'nowrap' }}>{record.user_phone || record.kyc_phone || '-'}</td>
                  <td style={{ padding: '6px', color: '#FFFFFF', fontSize: '12px' }}>{record.user_email || '-'}</td>
                  <td style={{ padding: '6px', color: '#FFFFFF', fontSize: '12px' }}>{record.user_name || '-'}</td>
                  <td style={{ padding: '6px', color: '#D1D5DB', fontSize: '12px' }}>{record.user_country || record.kyc_country_name || record.kyc_country_code || '-'}</td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>
                    <span style={{
                      padding: '2px 6px',
                      borderRadius: '12px',
                      fontSize: '10px',
                      fontWeight: '600',
                      backgroundColor: record.kyc_status === 'approved' ? '#065F46' : record.kyc_status === 'pending' ? '#92400E' : record.kyc_status === 'rejected' ? '#991B1B' : '#374151',
                      color: '#FFFFFF'
                    }}>
                      {record.kyc_status === 'approved' ? '✅' : record.kyc_status === 'pending' ? '⏳' : record.kyc_status === 'rejected' ? '❌' : '—'}
                    </span>
                  </td>
                  <td style={{ padding: '6px', color: '#FFFFFF', fontSize: '12px', textAlign: 'right' }}>${(record.usd_purchase_crypto || 0).toFixed(2)}</td>
                  <td style={{ padding: '6px', color: '#FFFFFF', fontSize: '12px', textAlign: 'right' }}>${(record.usd_purchase_zar || 0).toFixed(2)}</td>
                  <td style={{ padding: '6px', color: '#F59E0B', fontSize: '12px', fontWeight: '600', textAlign: 'right' }}>${(record.total_purchased_usdt || 0).toFixed(2)}</td>
                  <td style={{ padding: '6px', color: '#FFFFFF', fontSize: '12px', textAlign: 'right' }}>{(record.shares_purchased_monetary || 0).toFixed(2)}</td>
                  <td style={{ padding: '6px', color: '#FFFFFF', fontSize: '12px', textAlign: 'right' }}>{(record.shares_from_commission || 0).toFixed(2)}</td>
                  <td style={{ padding: '6px', color: (record.bonus_shares_15_percent || 0) > 0 ? '#FBBF24' : '#6B7280', fontSize: '12px', fontWeight: (record.bonus_shares_15_percent || 0) > 0 ? '600' : '400', textAlign: 'right' }}>{(record.bonus_shares_15_percent || 0) > 0 ? `+${(record.bonus_shares_15_percent || 0).toFixed(2)}` : '—'}</td>
                  <td style={{ padding: '6px', color: '#F59E0B', fontSize: '12px', fontWeight: '600', textAlign: 'right' }}>{(record.total_shares_decimal || 0).toFixed(2)}</td>
                  <td style={{ padding: '6px', color: '#FFFFFF', fontSize: '12px', textAlign: 'right' }}>${(record.commission_earned_usdt || 0).toFixed(2)}</td>
                  <td style={{ padding: '6px', color: '#FFFFFF', fontSize: '12px', textAlign: 'right' }}>${(record.commission_converted_to_shares || 0).toFixed(2)}</td>
                  <td style={{ padding: '6px', color: '#FFFFFF', fontSize: '12px', textAlign: 'right' }}>${(record.commission_withdrawals || 0).toFixed(2)}</td>
                  <td style={{ padding: '6px', color: (record.competition_prizes || 0) > 0 ? '#F59E0B' : '#6B7280', fontSize: '12px', fontWeight: (record.competition_prizes || 0) > 0 ? '600' : '400', textAlign: 'right' }}>
                    {(record.competition_prizes || 0) > 0 ? `$${(record.competition_prizes || 0).toFixed(2)}` : '—'}
                  </td>
                  <td style={{ padding: '6px', color: (record.manual_admin_additions || 0) > 0 ? '#C084FC' : '#6B7280', fontSize: '12px', fontWeight: (record.manual_admin_additions || 0) > 0 ? '600' : '400', textAlign: 'right' }}>
                    {(record.manual_admin_additions || 0) > 0 ? `$${(record.manual_admin_additions || 0).toFixed(2)}` : '—'}
                  </td>
                  <td style={{ padding: '6px', color: (record.admin_adjustments || 0) > 0 ? '#A78BFA' : (record.admin_adjustments || 0) < 0 ? '#FCA5A5' : '#6B7280', fontSize: '12px', textAlign: 'right' }}>
                    {(record.admin_adjustments || 0) !== 0 ? `$${(record.admin_adjustments || 0).toFixed(2)}` : '—'}
                  </td>
                  <td style={{ padding: '6px', color: record.available_balance_usdt < 0 ? '#FCA5A5' : '#10B981', fontSize: '12px', fontWeight: '600', textAlign: 'right' }}>
                    ${(record.available_balance_usdt || 0).toFixed(2)}
                  </td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>
                    {vulnFlags.length > 0 ? (
                      <span title={vulnFlags.join(', ')} style={{ color: '#EF4444', fontSize: '14px', cursor: 'help' }}>
                        🚩 {vulnFlags.length}
                      </span>
                    ) : (
                      <span style={{ color: '#10B981', fontSize: '12px' }}>✓</span>
                    )}
                  </td>
                  <td style={{ padding: '6px', textAlign: 'center' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setAuditingUser({ userId: record.user_id, username: record.user_name || record.user_email || `User ${record.user_id}` });
                      }}
                      style={{ backgroundColor: '#8B5CF6', color: '#FFFFFF', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}
                    >
                      Audit
                    </button>
                  </td>
                </tr>
                {expandedUserId === record.user_id && userDetails && (
                  <tr style={{ backgroundColor: '#111827', borderBottom: '1px solid #374151' }}>
                    <td colSpan={21} style={{ padding: '16px' }}>
                      {isJohan && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', backgroundColor: '#0F172A', border: '1px solid #F59E0B', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px' }}>
                          <span style={{ color: '#F59E0B', fontSize: '12px', fontWeight: 700, marginRight: '4px' }}>
                            🛡️ Johan-only user management:
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setJohanFullAuditUser({ userId: record.user_id, username: record.user_name || record.user_email || `User ${record.user_id}` });
                            }}
                            title="Full User Financial Movement Audit"
                            style={{ backgroundColor: '#F59E0B', color: '#000000', border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}
                          >
                            💰 Full $$$ Audit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setJohanEditUser({ userId: record.user_id, username: record.user_name || record.user_email || `User ${record.user_id}` });
                            }}
                            title="Edit User personal info & KYC (mirrors Certificate Manager)"
                            style={{ backgroundColor: '#10B981', color: '#FFFFFF', border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}
                          >
                            ✏️ Edit User &amp; KYC
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setJohanWalletUser({ userId: record.user_id, username: record.user_name || record.user_email || `User ${record.user_id}` });
                            }}
                            title="Manage user's locked crypto wallet"
                            style={{ backgroundColor: '#3B82F6', color: '#FFFFFF', border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}
                          >
                            🔐 Edit Locked Wallet
                          </button>
                        </div>
                      )}
                      {isOwner && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', backgroundColor: '#0F172A', border: '1px solid #EF4444', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px' }}>
                          <span style={{ color: '#EF4444', fontSize: '12px', fontWeight: 700, marginRight: '4px' }}>
                            🛡️ Owner actions:
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setReverseUser({ userId: record.user_id, username: record.user_name || record.user_email || `User ${record.user_id}` });
                            }}
                            title="Reverse an approved payment and reassign it to the correct user"
                            style={{ backgroundColor: '#EF4444', color: '#FFFFFF', border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}
                          >
                            🔄 Reverse Payment
                          </button>
                          {(() => {
                            const deletable = isDeletable(record);
                            const busy = deletingUserId === record.user_id;
                            return (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleScrubDelete(record); }}
                                disabled={!deletable || busy}
                                title={deletable
                                  ? 'Delete account & free email for reuse (financial data preserved)'
                                  : 'Only suspended or quarantined accounts can be deleted'}
                                style={{
                                  backgroundColor: deletable && !busy ? '#B91C1C' : '#374151',
                                  color: deletable && !busy ? '#FFFFFF' : '#9CA3AF',
                                  border: 'none', borderRadius: '4px', padding: '6px 10px',
                                  cursor: deletable && !busy ? 'pointer' : 'not-allowed',
                                  fontSize: '12px', fontWeight: 700, opacity: deletable && !busy ? 1 : 0.6
                                }}
                              >
                                {busy ? '⏳ Deleting…' : '🗑️ Delete Account'}
                              </button>
                            );
                          })()}
                        </div>
                      )}
                      <UserDetailsView
                        userDetails={userDetails}
                        detailsLoading={detailsLoading}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                        onProofClick={loadTransactionProof}
                        expandedUserId={expandedUserId}
                        auditRecord={record}
                        onEditCommission={async (commission: CommissionTransaction) => {
                          // Fetch the actual database record with all fields via API
                          try {
                            const sessionHeaders = await ensureSessionHeaders();
                            const response = await fetch('/api/admin/master-audit', {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                ...sessionHeaders
                              },
                              body: JSON.stringify({
                                operation: 'get_commission_record',
                                data: { commissionId: commission.transaction_id }
                              })
                            });

                            const result = await response.json();
                            if (!response.ok || !result.success) {
                              console.error('Error fetching commission record:', result.error);
                              setError('Failed to load commission record');
                              return;
                            }

                            setEditingRecord(result.data);
                            setRecordType('commission_transactions');
                          } catch (err) {
                            console.error('Error in onEditCommission:', err);
                            setError('Failed to load commission record');
                          }
                        }}
                        onDeleteCommission={async (commissionId: string) => {
                          if (window.confirm('Are you sure you want to delete this record? This cannot be undone.')) {
                            setDeletingCommissionId(commissionId);
                            try {
                              const response = await fetch('/api/admin/manual-record-editor', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json', ...(await ensureSessionHeaders()) },
                                body: JSON.stringify({
                                  action: 'DELETE',
                                  table: 'commission_transactions',
                                  recordId: commissionId,
                                  userId: currentUserId
                                })
                              });

                              if (!response.ok) {
                                throw new Error('Delete request failed');
                              }

                              if (expandedUserId) loadUserDetails(expandedUserId);
                              setSuccess('Commission record deleted successfully!');
                              setTimeout(() => setSuccess(''), 3000);
                            } catch (err) {
                              console.error('Delete failed:', err);
                              setError('Failed to delete record');
                            } finally {
                              setDeletingCommissionId(null);
                            }
                          }
                        }}
                        deletingCommissionId={deletingCommissionId}
                        onAddCommission={() => {
                          setRecordType('commission_transactions');
                          setCreatingRecord(true);
                        }}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ color: '#ffffff', fontSize: '14px', textAlign: 'center' }}>
        <p>Showing {filteredRecords.length} of {records.length} records</p>
      </div>

      {/* Transaction Proof Modal */}
      {proofModalOpen && selectedProof && (
        <TransactionProofModal
          proof={selectedProof}
          onClose={() => {
            setProofModalOpen(false);
            setSelectedProof(null);
          }}
          loading={proofLoading}
        />
      )}

      {/* Manual Record Editor Modal */}
      {editingRecord && (
        <ManualRecordEditor
          record={editingRecord}
          table={recordType}
          recordId={editingRecord.id || editingRecord.transaction_id}
          onClose={() => setEditingRecord(null)}
          onSave={(_updated) => {
            setEditingRecord(null);
            if (expandedUserId) loadUserDetails(expandedUserId);
            setSuccess('Record updated successfully!');
            setTimeout(() => setSuccess(''), 3000);
          }}
          onDelete={(_id) => {
            setEditingRecord(null);
            if (expandedUserId) loadUserDetails(expandedUserId);
            setSuccess('Record deleted successfully!');
            setTimeout(() => setSuccess(''), 3000);
          }}
        />
      )}

      {/* Manual Record Creator Modal */}
      {creatingRecord && recordType === 'commission_transactions' && expandedUserId ? (
        <CommissionRecordCreator
          referrerId={expandedUserId}
          onClose={() => setCreatingRecord(false)}
          onCreate={(_newRecord) => {
            setCreatingRecord(false);
            if (expandedUserId) loadUserDetails(expandedUserId);
            setSuccess('Commission record created successfully!');
            setTimeout(() => setSuccess(''), 3000);
          }}
        />
      ) : creatingRecord ? (
        <ManualRecordCreator
          table={recordType}
          fields={getFieldsForTable(recordType)}
          onClose={() => setCreatingRecord(false)}
          onCreate={(_newRecord) => {
            setCreatingRecord(false);
            if (expandedUserId) loadUserDetails(expandedUserId);
            setSuccess('Record created successfully!');
            setTimeout(() => setSuccess(''), 3000);
          }}
        />
      ) : null}

      {/* User Audit Panel Modal */}
      {auditingUser && (
        <UserAuditPanel
          userId={auditingUser.userId}
          username={auditingUser.username}
          onClose={() => setAuditingUser(null)}
          onRecordEdit={(recordType, record) => {
            setEditingRecord(record);
            setRecordType(recordType === 'purchase' ? 'aureus_share_purchases' :
                          recordType === 'transfer' ? 'share_transfers' :
                          recordType === 'conversion' ? 'commission_conversions' :
                          recordType === 'presale_commission' ? 'commission_transactions' :
                          recordType === 'multilevel_commission' ? 'multi_level_commissions' :
                          recordType);
          }}
        />
      )}

      {/* Full User Financial Movement Audit — Johan only */}
      {isJohan && johanFullAuditUser && (
        <FullFinancialAuditReport
          userId={johanFullAuditUser.userId}
          username={johanFullAuditUser.username}
          onClose={() => setJohanFullAuditUser(null)}
        />
      )}

      {/* Edit User & KYC Modal — Johan only (mirrors Certificate Manager) */}
      {isJohan && johanEditUser && (
        <div
          onClick={() => setJohanEditUser(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#1F2937', border: '2px solid #10B981', borderRadius: '8px', maxWidth: '1200px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #374151', position: 'sticky', top: 0, backgroundColor: '#1F2937', zIndex: 1 }}>
              <div style={{ color: '#10B981', fontWeight: 700, fontSize: '15px' }}>
                ✏️ Edit User &amp; KYC — {johanEditUser.username} (User ID {johanEditUser.userId})
              </div>
              <button
                onClick={() => setJohanEditUser(null)}
                style={{ backgroundColor: '#374151', color: '#FFFFFF', border: 'none', borderRadius: '4px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}
              >
                ✕ Close
              </button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <AdminUserEditor
                userId={johanEditUser.userId}
                adminEmail={currentAdminEmail}
                onSaved={() => { if (expandedUserId) loadUserDetails(expandedUserId); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Locked Wallet Admin Modal — Johan only */}
      {isJohan && johanWalletUser && (
        <div
          onClick={() => setJohanWalletUser(null)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: '#1F2937', border: '2px solid #3B82F6', borderRadius: '8px', maxWidth: '1400px', width: '100%', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #374151', position: 'sticky', top: 0, backgroundColor: '#1F2937', zIndex: 1 }}>
              <div style={{ color: '#3B82F6', fontWeight: 700, fontSize: '15px' }}>
                🔐 Locked Wallet Management — {johanWalletUser.username} (User ID {johanWalletUser.userId})
              </div>
              <button
                onClick={() => setJohanWalletUser(null)}
                style={{ backgroundColor: '#374151', color: '#FFFFFF', border: 'none', borderRadius: '4px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 700 }}
              >
                ✕ Close
              </button>
            </div>
            <div style={{ padding: '12px 16px' }}>
              <LockedWalletAdmin initialUserId={johanWalletUser.userId} />
            </div>
          </div>
        </div>
      )}

      {/* Reverse Payment Modal — owner only (Johan/JP/Ruvan) */}
      {isOwner && reverseUser && (
        <ReversePaymentModal
          userId={reverseUser.userId}
          username={reverseUser.username}
          adminUserId={currentUserId ?? undefined}
          onClose={() => setReverseUser(null)}
          onSuccess={() => { loadAuditData(); if (expandedUserId) loadUserDetails(expandedUserId); }}
        />
      )}

      </>}
    </div>
  );
}

function UserDetailsView({ userDetails, detailsLoading, activeTab, setActiveTab, onProofClick, onEditCommission, onDeleteCommission, onAddCommission, deletingCommissionId, auditRecord }: any) {
  if (detailsLoading) {
    return <div style={{ color: '#ffffff' }}>Loading transaction details...</div>;
  }

  const tabs = ['purchases', 'commissions', 'conversions', 'transfers', 'kyc'] as const;

  return (
    <div>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #374151', paddingBottom: '8px' }}>
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '8px 16px',
              backgroundColor: activeTab === tab ? (tab === 'kyc' ? '#8B5CF6' : '#3B82F6') : 'transparent',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: activeTab === tab ? '600' : '400',
            }}
          >
            {tab === 'kyc' ? '🪪 KYC' : tab.charAt(0).toUpperCase() + tab.slice(1)} {tab !== 'kyc' && `(${
              tab === 'purchases' ? userDetails.purchases.length :
              tab === 'commissions' ? userDetails.commissions.length :
              tab === 'conversions' ? userDetails.conversions.length :
              userDetails.transfers.length
            })`}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'purchases' && <PurchasesTable purchases={userDetails.purchases} onProofClick={onProofClick} />}
      {activeTab === 'commissions' && (
        <>
          <button
            onClick={onAddCommission}
            style={{
              marginBottom: '16px',
              padding: '10px 20px',
              backgroundColor: '#10B981',
              color: '#FFFFFF',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600'
            }}
          >
            ➕ Add Commission
          </button>
          <CommissionsTable
            commissions={userDetails.commissions}
            onEdit={onEditCommission}
            onDelete={onDeleteCommission}
            deletingCommissionId={deletingCommissionId}
          />
        </>
      )}
      {activeTab === 'conversions' && <ConversionsTable conversions={userDetails.conversions} />}
      {activeTab === 'transfers' && <TransfersTable transfers={userDetails.transfers} />}
      {activeTab === 'kyc' && auditRecord && (
        <div style={{ backgroundColor: '#1F2937', borderRadius: '8px', padding: '20px' }}>
          <h4 style={{ color: '#FFFFFF', fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>
            🪪 KYC Information — {auditRecord.kyc_status === 'approved' ? '✅ Approved' : auditRecord.kyc_status === 'pending' ? '⏳ Pending' : auditRecord.kyc_status === 'rejected' ? '❌ Rejected' : '⚠️ No KYC Submitted'}
          </h4>
          {auditRecord.kyc_status === 'none' ? (
            <p style={{ color: '#9CA3AF' }}>This user has not submitted any KYC information.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              {[
                { label: 'Full Legal Name', value: auditRecord.kyc_full_name },
                { label: 'Email', value: auditRecord.kyc_email },
                { label: 'Phone', value: auditRecord.kyc_phone },
                { label: 'ID Type', value: auditRecord.kyc_id_type },
                { label: 'Country', value: auditRecord.kyc_country_name || auditRecord.kyc_country_code },
                { label: 'City', value: auditRecord.kyc_city },
                { label: 'Address', value: auditRecord.kyc_address },
                { label: 'Postal Code', value: auditRecord.kyc_postal_code },
                { label: 'KYC Submitted', value: auditRecord.kyc_created_at ? new Date(auditRecord.kyc_created_at).toLocaleDateString() : null },
                { label: 'Last Updated', value: auditRecord.kyc_updated_at ? new Date(auditRecord.kyc_updated_at).toLocaleDateString() : null },
              ].filter(item => item.value).map(item => (
                <div key={item.label} style={{ backgroundColor: '#111827', borderRadius: '6px', padding: '12px' }}>
                  <div style={{ color: '#9CA3AF', fontSize: '12px', marginBottom: '4px' }}>{item.label}</div>
                  <div style={{ color: '#FFFFFF', fontSize: '14px', fontWeight: '500' }}>{item.value}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Human-readable label for a bonus/gift row, derived from package_name / payment_method.
function bonusTypeLabel(packageName?: string, paymentMethod?: string): string {
  const pkg = (packageName || '').toLowerCase();
  const pm = (paymentMethod || '').toLowerCase();
  if (pkg.includes('single share special') || pm.includes('single_special')) return 'Single-Share Special';
  if (pkg.includes('birthday') || pm.includes('birthday')) return 'Anniversary / Birthday';
  if (pkg.includes('bogo') || pm.includes('bogo')) return 'BOGO';
  if (pm.includes('_upline_gift')) return 'Upline Gift';
  if (pm.includes('_buyer_gift')) return 'Buyer Gift';
  return 'Bonus';
}

function PurchasesTable({ purchases, onProofClick }: { purchases: SharePurchase[], onProofClick?: (ref: string, method: string) => void }) {
  if (purchases.length === 0) {
    return <div style={{ color: '#ffffff', padding: '16px' }}>No share purchases found</div>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ backgroundColor: '#1F2937', borderBottom: '1px solid #374151' }}>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Date</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Package</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Shares</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#FBBF24', fontWeight: '600' }} title="Bonus / gift shares gained on this purchase (BOGO, Anniversary, Single-Share Special, etc.). Stored in bonus_shares.">🎁 Bonus</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Amount</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Method</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Status</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Ref ID</th>
          </tr>
        </thead>
        <tbody>
          {purchases.map((p) => {
            const bonus = Number(p.display_bonus_shares) || displayRowBonusShares(p);
            const purchased = Number(p.shares_purchased) || 0;
            return (
            <tr key={p.purchase_id} style={{ borderBottom: '1px solid #374151' }}>
              <td style={{ padding: '8px', color: '#FFFFFF' }}>{new Date(p.purchase_date).toLocaleDateString()}</td>
              <td style={{ padding: '8px', color: '#FFFFFF' }}>{p.package_name}</td>
              <td style={{ padding: '8px', color: '#FFFFFF' }}>{purchased.toFixed(2)}</td>
              <td style={{ padding: '8px', color: bonus > 0 ? '#FBBF24' : '#6B7280', fontWeight: bonus > 0 ? '600' : '400' }}>
                {bonus > 0
                  ? <span title={`${bonusTypeLabel(p.package_name, p.payment_method)} — ${bonus.toFixed(2)} bonus shares`}>🎁 +{bonus.toFixed(2)} <span style={{ color: '#9CA3AF', fontWeight: '400' }}>({bonusTypeLabel(p.package_name, p.payment_method)})</span></span>
                  : '—'}
              </td>
              <td style={{ padding: '8px', color: '#FFFFFF' }}>${p.total_amount.toFixed(2)}</td>
              <td style={{ padding: '8px', color: '#FFFFFF', fontSize: '14px' }}>{p.payment_method}</td>
              <td style={{ padding: '8px', color: p.status === 'active' ? '#86EFAC' : '#FCA5A5' }}>{p.status}</td>
              <td
                style={{
                  padding: '8px',
                  color: p.transaction_reference ? '#60A5FA' : '#9CA3AF',
                  fontSize: '14px',
                  cursor: p.transaction_reference ? 'pointer' : 'default',
                  textDecoration: p.transaction_reference ? 'underline' : 'none'
                }}
                title={p.transaction_reference || 'No transaction reference'}
                onClick={() => p.transaction_reference && onProofClick?.(p.transaction_reference, p.payment_method)}
              >
                {p.transaction_reference ? p.transaction_reference.substring(0, 8) + '...' : '-'}
              </td>
            </tr>
          );})}
        </tbody>
      </table>
    </div>
  );
}

function CommissionsTable({
  commissions,
  onEdit,
  onDelete,
  deletingCommissionId
}: {
  commissions: CommissionTransaction[],
  onEdit?: (commission: CommissionTransaction) => void,
  onDelete?: (commissionId: string) => void,
  deletingCommissionId?: string | null
}) {
  if (commissions.length === 0) {
    return <div style={{ color: '#ffffff', padding: '16px' }}>No commission transactions found</div>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ backgroundColor: '#1F2937', borderBottom: '1px solid #374151' }}>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Date</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Referred User</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Purchase Amount</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>USDT Commission</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Share Commission</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Status</th>
            <th style={{ padding: '8px', textAlign: 'center', color: '#ffffff', fontWeight: '600' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {commissions.map((c) => (
            <tr key={c.transaction_id} style={{ borderBottom: '1px solid #374151' }}>
              <td style={{ padding: '8px', color: '#FFFFFF' }}>{new Date(c.created_at).toLocaleDateString()}</td>
              <td style={{ padding: '8px', color: '#FFFFFF', fontSize: '14px' }}>
                {c.referred_user_name} (ID: {c.referred_user_id})
              </td>
              <td style={{ padding: '8px', color: '#FFFFFF' }}>${c.share_purchase_amount.toFixed(2)}</td>
              <td style={{ padding: '8px', color: '#86EFAC' }}>${c.usdt_commission.toFixed(2)}</td>
              <td style={{ padding: '8px', color: '#86EFAC' }}>{c.share_commission.toFixed(2)} shares</td>
              <td style={{ padding: '8px', color: c.status === 'approved' ? '#86EFAC' : '#FCA5A5' }}>{c.status}</td>
              <td style={{ padding: '8px', textAlign: 'center' }}>
                <button
                  onClick={() => onEdit?.(c)}
                  title="Edit this commission record"
                  style={{
                    backgroundColor: '#3B82F6',
                    color: '#FFFFFF',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    marginRight: '4px'
                  }}
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => onDelete?.(c.transaction_id)}
                  title="Delete this commission record"
                  disabled={deletingCommissionId === c.transaction_id}
                  style={{
                    backgroundColor: deletingCommissionId === c.transaction_id ? '#4B5563' : '#7F1D1D',
                    color: deletingCommissionId === c.transaction_id ? '#9CA3AF' : '#FCA5A5',
                    border: 'none',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: deletingCommissionId === c.transaction_id ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    opacity: deletingCommissionId === c.transaction_id ? 0.7 : 1
                  }}
                >
                  {deletingCommissionId === c.transaction_id ? '⏳ Deleting...' : '🗑️ Delete'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ConversionsTable({ conversions }: { conversions: CommissionConversion[] }) {
  if (conversions.length === 0) {
    return <div style={{ color: '#ffffff', padding: '16px' }}>No commission conversions found</div>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ backgroundColor: '#1F2937', borderBottom: '1px solid #374151' }}>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Date</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Shares Requested</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>USDT Deducted</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Share Price</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Status</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Approved</th>
          </tr>
        </thead>
        <tbody>
          {conversions.map((c) => (
            <tr key={c.conversion_id} style={{ borderBottom: '1px solid #374151' }}>
              <td style={{ padding: '8px', color: '#FFFFFF' }}>{new Date(c.created_at).toLocaleDateString()}</td>
              <td style={{ padding: '8px', color: '#FFFFFF' }}>{c.shares_requested}</td>
              <td style={{ padding: '8px', color: '#FCA5A5' }}>${c.usdt_amount.toFixed(2)}</td>
              <td style={{ padding: '8px', color: '#FFFFFF' }}>${c.share_price.toFixed(2)}</td>
              <td style={{ padding: '8px', color: c.status === 'approved' ? '#86EFAC' : c.status === 'pending' ? '#FBBF24' : '#FCA5A5' }}>
                {c.status}
              </td>
              <td style={{ padding: '8px', color: '#FFFFFF', fontSize: '14px' }}>
                {c.approved_at ? new Date(c.approved_at).toLocaleDateString() : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TransfersTable({ transfers }: { transfers: ShareTransfer[] }) {
  if (transfers.length === 0) {
    return <div style={{ color: '#ffffff', padding: '16px' }}>No share transfers found</div>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ backgroundColor: '#1F2937', borderBottom: '1px solid #374151' }}>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Date</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Type</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Other User</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Shares</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Reason</th>
            <th style={{ padding: '8px', textAlign: 'left', color: '#ffffff', fontWeight: '600' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {transfers.map((t) => (
            <tr key={t.transfer_id} style={{ borderBottom: '1px solid #374151' }}>
              <td style={{ padding: '8px', color: '#FFFFFF' }}>{new Date(t.created_at).toLocaleDateString()}</td>
              <td style={{ padding: '8px', color: t.transfer_type === 'Sent' ? '#FCA5A5' : '#86EFAC', fontWeight: '600' }}>
                {t.transfer_type}
              </td>
              <td style={{ padding: '8px', color: '#FFFFFF', fontSize: '14px' }}>
                {t.other_user_name} (ID: {t.other_user_id})
              </td>
              <td style={{ padding: '8px', color: '#FFFFFF' }}>{t.shares_transferred}</td>
              <td style={{ padding: '8px', color: '#FFFFFF', fontSize: '14px' }}>{t.transfer_reason || '-'}</td>
              <td style={{ padding: '8px', color: t.status === 'completed' ? '#86EFAC' : '#FCA5A5' }}>{t.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TransactionProofModal({ proof, onClose, loading }: { proof: TransactionProof, onClose: () => void, loading: boolean }) {
  const [imageZoom, setImageZoom] = useState(1);
  // Issue #22 Phase 2b: resolve the raw screenshot_url to a short-lived signed
  // URL before rendering. Wave 4: no public-URL fallback.
  const [signedScreenshotUrl, setSignedScreenshotUrl] = useState<string | null>(null);
  const isCrypto = proof.network && proof.network !== 'BANK_TRANSFER';
  const isBank = proof.network === 'BANK_TRANSFER' || proof.payment_method === 'bank_transfer';

  useEffect(() => {
    let cancelled = false;
    setSignedScreenshotUrl(null);
    if (proof.screenshot_url) {
      resolveProofUrl(proof.screenshot_url).then((url) => {
        if (!cancelled) setSignedScreenshotUrl(url);
      });
    }
    return () => { cancelled = true; };
  }, [proof.screenshot_url]);

  const getBlockchainExplorerUrl = () => {
    if (!proof.transaction_hash) return null;

    const network = proof.network?.toLowerCase() || '';
    const hash = proof.transaction_hash;

    if (network.includes('pol') || network.includes('polygon') || network.includes('matic')) return `https://polygonscan.com/tx/${hash}`;
    if (network.includes('bsc') || network.includes('binance')) return `https://bscscan.com/tx/${hash}`;
    if (network.includes('ethereum') || network === 'eth') return `https://etherscan.io/tx/${hash}`;
    if (network.includes('arbitrum')) return `https://arbiscan.io/tx/${hash}`;

    return null;
  };

  const explorerUrl = getBlockchainExplorerUrl();

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#1F2937',
        border: '1px solid #374151',
        borderRadius: '12px',
        maxWidth: '800px',
        width: '100%',
        maxHeight: '90vh',
        overflowY: 'auto',
        padding: '24px'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#FFFFFF', margin: 0 }}>
            🔍 Transaction Proof Details
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#ffffff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: 0,
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ✕
          </button>
        </div>

        {loading ? (
          <div style={{ color: '#ffffff', textAlign: 'center', padding: '32px' }}>
            Loading transaction details...
          </div>
        ) : (
          <div>
            {/* Transaction Type Badge */}
            <div style={{ marginBottom: '16px' }}>
              <span style={{
                display: 'inline-block',
                padding: '6px 12px',
                backgroundColor: isCrypto ? '#3B82F6' : '#F59E0B',
                color: '#FFFFFF',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '600'
              }}>
                {isCrypto ? '🔗 Cryptocurrency' : '🏦 Bank Transfer'}
              </span>
            </div>

            {/* Crypto Transaction Details */}
            {isCrypto && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600', marginBottom: '12px', textTransform: 'uppercase' }}>
                  Blockchain Details
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>Network</p>
                    <p style={{ color: '#FFFFFF', fontSize: '14px', margin: 0, fontWeight: '600' }}>{proof.network || '-'}</p>
                  </div>
                  <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>Status</p>
                    <p style={{ color: proof.status === 'approved' ? '#86EFAC' : '#FCA5A5', fontSize: '14px', margin: 0, fontWeight: '600' }}>
                      {proof.status || '-'}
                    </p>
                  </div>
                </div>

                <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>
                  <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>Transaction Hash</p>
                  <p style={{ color: '#60A5FA', fontSize: '14px', margin: 0, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                    {proof.transaction_hash || '-'}
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>From (Sender)</p>
                    <p style={{ color: '#FFFFFF', fontSize: '13px', margin: 0, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {proof.sender_wallet || '-'}
                    </p>
                  </div>
                  <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>To (Receiver)</p>
                    <p style={{ color: '#FFFFFF', fontSize: '13px', margin: 0, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {proof.receiver_wallet || '-'}
                    </p>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>Amount</p>
                    <p style={{ color: '#86EFAC', fontSize: '14px', margin: 0, fontWeight: '600' }}>
                      {proof.amount ? `${proof.amount} ${proof.currency || 'USDT'}` : '-'}
                    </p>
                  </div>
                  <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>Date</p>
                    <p style={{ color: '#FFFFFF', fontSize: '14px', margin: 0 }}>
                      {proof.created_at ? new Date(proof.created_at).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>

                {/* Blockchain Explorer Link */}
                {explorerUrl && (
                  <div style={{ marginTop: '16px' }}>
                    <a
                      href={explorerUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'inline-block',
                        padding: '10px 16px',
                        backgroundColor: '#3B82F6',
                        color: '#FFFFFF',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      🔗 View on Blockchain Explorer
                    </a>
                  </div>
                )}

                {/* Screenshot */}
                {signedScreenshotUrl && (
                  <div style={{ marginTop: '16px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Transaction Screenshot
                    </p>
                    <div style={{
                      backgroundColor: '#111827',
                      padding: '12px',
                      borderRadius: '6px',
                      overflow: 'auto',
                      maxHeight: '400px'
                    }}>
                      <img
                        src={signedScreenshotUrl || undefined}
                        alt="Transaction screenshot"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '4px',
                          transform: `scale(${imageZoom})`,
                          transformOrigin: 'top left',
                          cursor: 'zoom-in'
                        }}
                        onClick={() => setImageZoom(imageZoom === 1 ? 1.5 : 1)}
                        title="Click to zoom"
                      />
                    </div>
                    <p style={{ color: '#ffffff', fontSize: '14px', marginTop: '8px' }}>
                      💡 Click image to zoom (1x / 1.5x)
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Bank Transfer Details */}
            {isBank && (
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600', marginBottom: '12px', textTransform: 'uppercase' }}>
                  Bank Transfer Details
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>Status</p>
                    <p style={{ color: proof.status === 'approved' ? '#86EFAC' : '#FCA5A5', fontSize: '14px', margin: 0, fontWeight: '600' }}>
                      {proof.status || '-'}
                    </p>
                  </div>
                  <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>Amount</p>
                    <p style={{ color: '#86EFAC', fontSize: '14px', margin: 0, fontWeight: '600' }}>
                      {proof.amount ? `${proof.amount} ${proof.currency || 'ZAR'}` : '-'}
                    </p>
                  </div>
                </div>

                <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px', marginBottom: '16px' }}>
                  <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>Reference Number</p>
                  <p style={{ color: '#60A5FA', fontSize: '14px', margin: 0, fontFamily: 'monospace' }}>
                    {proof.id || '-'}
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>Recipient Account</p>
                    <p style={{ color: '#FFFFFF', fontSize: '13px', margin: 0, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {proof.receiver_wallet || '-'}
                    </p>
                  </div>
                  <div style={{ backgroundColor: '#111827', padding: '12px', borderRadius: '6px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', margin: '0 0 4px 0' }}>Date</p>
                    <p style={{ color: '#FFFFFF', fontSize: '14px', margin: 0 }}>
                      {proof.created_at ? new Date(proof.created_at).toLocaleString() : '-'}
                    </p>
                  </div>
                </div>

                {/* Bank Proof Image */}
                {signedScreenshotUrl && (
                  <div style={{ marginTop: '16px' }}>
                    <p style={{ color: '#ffffff', fontSize: '14px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase' }}>
                      Proof of Payment
                    </p>
                    <div style={{
                      backgroundColor: '#111827',
                      padding: '12px',
                      borderRadius: '6px',
                      overflow: 'auto',
                      maxHeight: '400px'
                    }}>
                      <img
                        src={signedScreenshotUrl || undefined}
                        alt="Bank transfer proof"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '4px',
                          transform: `scale(${imageZoom})`,
                          transformOrigin: 'top left',
                          cursor: 'zoom-in'
                        }}
                        onClick={() => setImageZoom(imageZoom === 1 ? 1.5 : 1)}
                        title="Click to zoom"
                      />
                    </div>
                    <p style={{ color: '#ffffff', fontSize: '14px', marginTop: '8px' }}>
                      💡 Click image to zoom (1x / 1.5x)
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Close Button */}
            <div style={{ marginTop: '24px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#374151',
                  color: '#FFFFFF',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

